import React, { useState, useEffect, useMemo } from 'react';
import { Link2, Plus, Trash2, Save, Building2, RefreshCw, Factory, Users, Briefcase, CloudDownload, Euro, FileText } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';

// Mapping types configuration - labels use translation keys, resolved at render time
const MAPPING_TYPES_CONFIG = [
  {
    key: 'cost_center',
    labelKey: 'admin.mapping.costCenters',
    icon: Building2,
    codeLabelKey: 'admin.mapping.costCenterCode',
    nameLabelKey: 'admin.mapping.costCenterLabel',
    codePlaceholder: 'z.B. 110700',
    namePlaceholder: 'z.B. IT-Abteilung',
    color: 'cyan'
  },
  {
    key: 'plant',
    labelKey: 'admin.mapping.plants',
    icon: Factory,
    codeLabelKey: 'admin.mapping.plantCode',
    nameLabelKey: 'admin.mapping.plantLabel',
    codePlaceholder: 'z.B. 1000',
    namePlaceholder: 'z.B. Hauptwerk',
    color: 'green'
  },
  {
    key: 'purchasing_org',
    labelKey: 'admin.mapping.purchasingOrgs',
    icon: Briefcase,
    codeLabelKey: 'admin.mapping.purchasingOrgCode',
    nameLabelKey: 'admin.mapping.purchasingOrgLabel',
    codePlaceholder: 'z.B. 1000',
    namePlaceholder: 'z.B. Zentrale Beschaffung',
    color: 'purple'
  },
  {
    key: 'purchasing_group',
    labelKey: 'admin.mapping.purchasingGroups',
    icon: Users,
    codeLabelKey: 'admin.mapping.purchasingGroupCode',
    nameLabelKey: 'admin.mapping.purchasingGroupLabel',
    codePlaceholder: 'z.B. EK01',
    namePlaceholder: 'z.B. Einkauf IT',
    color: 'orange'
  },
  {
    key: 'gl_account',
    labelKey: 'admin.mapping.glAccounts',
    icon: Euro,
    codeLabelKey: 'admin.mapping.glAccountCode',
    nameLabelKey: 'admin.mapping.glAccountLabel',
    codePlaceholder: 'z.B. 400000',
    namePlaceholder: 'z.B. Materialaufwand',
    color: 'brand'
  },
  {
    key: 'order',
    labelKey: 'admin.mapping.orders',
    icon: FileText,
    codeLabelKey: 'admin.mapping.orderCode',
    nameLabelKey: 'admin.mapping.orderLabel',
    codePlaceholder: 'z.B. 100001',
    namePlaceholder: 'z.B. Innenauftrag IT',
    color: 'brand'
  }
];

const MappingSettings = ({ user, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('cost_center');
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState({});
  const [saving, setSaving] = useState(false);
  const [newMapping, setNewMapping] = useState({ code: '', name: '' });

  // Resolve translation keys for mapping types
  const MAPPING_TYPES = useMemo(() => MAPPING_TYPES_CONFIG.map(type => ({
    ...type,
    label: t(type.labelKey),
    codeLabel: t(type.codeLabelKey),
    nameLabel: t(type.nameLabelKey),
  })), [t]);

  // Get current mapping type config
  const currentType = MAPPING_TYPES.find(mt => mt.key === activeTab);

  // Load mappings for a specific type
  const loadMappings = async (type) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    try {
      const response = await fetch(`/api/admin/mappings/${type}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setMappings(prev => ({ ...prev, [type]: data }));
      } else {
        console.error(`Failed to load ${type} mappings`);
        showError?.(t('admin.mapping.loadError', { type: currentType?.label || '' }));
      }
    } catch (error) {
      console.error(`Error loading ${type} mappings:`, error);
      showError?.(t('admin.mapping.loadError', { type: currentType?.label || '' }));
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // Load mappings when tab changes
  useEffect(() => {
    if (user?.token && !mappings[activeTab]) {
      loadMappings(activeTab);
    }
  }, [activeTab, user?.token]);

  // Add new mapping
  const handleAddMapping = async () => {
    if (!newMapping.code || !newMapping.name) {
      showError?.(t('admin.mapping.fillBothFields'));
      return;
    }

    const currentMappings = mappings[activeTab] || [];
    if (currentMappings.some(m => m.code === newMapping.code)) {
      showError?.(t('admin.mapping.alreadyExists', { type: currentType.codeLabel }));
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`/api/admin/mappings/${activeTab}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(newMapping)
      });

      if (response.ok) {
        const savedMapping = await response.json();
        setMappings(prev => ({
          ...prev,
          [activeTab]: [...(prev[activeTab] || []), savedMapping]
        }));
        setNewMapping({ code: '', name: '' });
        showSuccess?.(t('admin.mapping.added'));
      } else {
        const error = await response.json();
        showError?.(error.message || t('admin.mapping.addError'));
      }
    } catch (error) {
      console.error('Error adding mapping:', error);
      showError?.(t('admin.mapping.addError'));
    } finally {
      setSaving(false);
    }
  };

  // Update existing mapping locally
  const handleUpdateMapping = async (id, field, value) => {
    setMappings(prev => ({
      ...prev,
      [activeTab]: (prev[activeTab] || []).map(m =>
        m.id === id ? { ...m, [field]: value } : m
      )
    }));
  };

  // Save updated mapping
  const handleSaveMapping = async (mapping) => {
    setSaving(true);
    try {
      const response = await fetch(`/api/admin/mappings/${activeTab}/${mapping.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          code: mapping.code,
          name: mapping.name
        })
      });

      if (response.ok) {
        showSuccess?.(t('admin.mapping.updated'));
      } else {
        const error = await response.json();
        showError?.(error.message || t('admin.mapping.updateError'));
      }
    } catch (error) {
      console.error('Error updating mapping:', error);
      showError?.(t('admin.mapping.updateError'));
    } finally {
      setSaving(false);
    }
  };

  // Delete mapping
  const handleDeleteMapping = async (id) => {
    if (!window.confirm(t('admin.mapping.deleteConfirm'))) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/mappings/${activeTab}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      if (response.ok) {
        setMappings(prev => ({
          ...prev,
          [activeTab]: (prev[activeTab] || []).filter(m => m.id !== id)
        }));
        showSuccess?.(t('admin.mapping.deleted'));
      } else {
        showError?.(t('admin.mapping.deleteError'));
      }
    } catch (error) {
      console.error('Error deleting mapping:', error);
      showError?.(t('admin.mapping.deleteError'));
    }
  };

  // Get color classes for current type
  const getColorClasses = (color) => {
    const colors = {
      cyan: {
        tabActive: isDarkMode ? 'bg-cyan-600/30 text-cyan-300 border-cyan-400' : 'bg-brand/10 text-brand border-brand',
        iconBg: isDarkMode ? 'from-cyan-600/60 via-cyan-700/50 to-cyan-800/60' : 'from-brand/80 via-brand to-[#1D4ED8]/80',
        button: isDarkMode
          ? 'linear-gradient(135deg, rgba(6,182,212,0.7) 0%, rgba(8,145,178,0.7) 50%, rgba(14,116,144,0.7) 100%)'
          : undefined,
        buttonShadow: isDarkMode ? '0 8px 32px rgba(6,182,212,0.4)' : undefined,
        ring: isDarkMode ? 'focus:ring-cyan-400/50' : 'focus:ring-brand/40',
        saveBtn: isDarkMode ? 'bg-cyan-600/30 hover:bg-cyan-600/50 text-cyan-300' : 'bg-brand/10 hover:bg-brand/20 text-brand'
      },
      green: {
        tabActive: isDarkMode ? 'bg-green-600/30 text-green-300 border-green-400' : 'bg-brand/10 text-brand border-brand',
        iconBg: isDarkMode ? 'from-green-600/60 via-green-700/50 to-green-800/60' : 'from-brand/80 via-brand to-[#1D4ED8]/80',
        button: isDarkMode
          ? 'linear-gradient(135deg, rgba(34,197,94,0.7) 0%, rgba(22,163,74,0.7) 50%, rgba(21,128,61,0.7) 100%)'
          : undefined,
        buttonShadow: isDarkMode ? '0 8px 32px rgba(34,197,94,0.4)' : undefined,
        ring: isDarkMode ? 'focus:ring-green-400/50' : 'focus:ring-brand/40',
        saveBtn: isDarkMode ? 'bg-green-600/30 hover:bg-green-600/50 text-green-300' : 'bg-brand/10 hover:bg-brand/20 text-brand'
      },
      purple: {
        tabActive: isDarkMode ? 'bg-purple-600/30 text-purple-300 border-purple-400' : 'bg-brand/10 text-brand border-brand',
        iconBg: isDarkMode ? 'from-purple-600/60 via-purple-700/50 to-purple-800/60' : 'from-brand/80 via-brand to-[#1D4ED8]/80',
        button: isDarkMode
          ? 'linear-gradient(135deg, rgba(147,51,234,0.7) 0%, rgba(126,34,206,0.7) 50%, rgba(107,33,168,0.7) 100%)'
          : undefined,
        buttonShadow: isDarkMode ? '0 8px 32px rgba(147,51,234,0.4)' : undefined,
        ring: isDarkMode ? 'focus:ring-purple-400/50' : 'focus:ring-brand/40',
        saveBtn: isDarkMode ? 'bg-purple-600/30 hover:bg-purple-600/50 text-purple-300' : 'bg-brand/10 hover:bg-brand/20 text-brand'
      },
      orange: {
        tabActive: isDarkMode ? 'bg-orange-600/30 text-orange-300 border-orange-400' : 'bg-brand/10 text-brand border-brand',
        iconBg: isDarkMode ? 'from-orange-600/60 via-orange-700/50 to-orange-800/60' : 'from-brand/80 via-brand to-[#1D4ED8]/80',
        button: isDarkMode
          ? 'linear-gradient(135deg, rgba(249,115,22,0.7) 0%, rgba(234,88,12,0.7) 50%, rgba(194,65,12,0.7) 100%)'
          : undefined,
        buttonShadow: isDarkMode ? '0 8px 32px rgba(249,115,22,0.4)' : undefined,
        ring: isDarkMode ? 'focus:ring-orange-400/50' : 'focus:ring-brand/40',
        saveBtn: isDarkMode ? 'bg-orange-600/30 hover:bg-orange-600/50 text-orange-300' : 'bg-brand/10 hover:bg-brand/20 text-brand'
      },
      brand: {
        tabActive: isDarkMode ? 'bg-blue-600/30 text-blue-300 border-blue-400' : 'bg-brand/10 text-brand border-brand',
        iconBg: 'from-brand/80 via-brand to-[#1D4ED8]/80',
        button: isDarkMode
          ? 'linear-gradient(135deg, rgba(37,99,235,0.7) 0%, rgba(29,78,216,0.7) 50%, rgba(30,64,175,0.7) 100%)'
          : undefined,
        buttonShadow: isDarkMode ? '0 8px 32px rgba(37,99,235,0.4)' : undefined,
        ring: 'focus:ring-brand/40',
        saveBtn: isDarkMode ? 'bg-blue-600/30 hover:bg-blue-600/50 text-blue-300' : 'bg-brand/10 hover:bg-brand/20 text-brand'
      }
    };
    return colors[color] || colors.cyan;
  };

  const colorClasses = getColorClasses(currentType?.color || 'cyan');
  const currentMappings = mappings[activeTab] || [];
  const isLoading = loading[activeTab];
  const TypeIcon = currentType?.icon || Link2;

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div className="relative group/header">
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
          : 'glass-light-card'
        } rounded-3xl`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : 'bg-gradient-to-t from-blue-50/20 via-transparent to-white/30'
        } rounded-3xl`}></div>

        <div className="relative p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm"></div>
                <div className={`relative bg-gradient-to-br ${colorClasses.iconBg} backdrop-blur-xl w-10 h-10 rounded-xl flex items-center justify-center shadow-lg border border-white/20`}>
                  <TypeIcon className="w-5 h-5 text-white drop-shadow-lg" />
                </div>
              </div>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>
                  {t('admin.mapping.title')}
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                  {t('admin.mapping.subtitle')}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* SAP Sync Button */}
              <button
                onClick={() => showSuccess?.(t('admin.mapping.sapSyncFuture'))}
                className={`relative group/sap overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8]'}`}
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(59,130,246,0.6) 0%, rgba(37,99,235,0.6) 50%, rgba(29,78,216,0.6) 100%)'
                    : undefined,
                  backdropFilter: isDarkMode ? 'blur(20px)' : undefined,
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : undefined,
                  boxShadow: isDarkMode
                    ? '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                    : undefined
                }}
              >
                <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white">
                  <CloudDownload className="w-4 h-4 mr-2" />
                  {t('admin.mapping.syncSap')}
                </div>
              </button>

              {/* Refresh Button */}
              <button
                onClick={() => loadMappings(activeTab)}
                disabled={isLoading}
                className={`relative group/refresh overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 border border-slate-200'}`}
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)'
                    : undefined,
                  backdropFilter: isDarkMode ? 'blur(20px)' : undefined,
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : undefined,
                  boxShadow: isDarkMode
                    ? '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                    : undefined
                }}
              >
                <div className={`relative z-10 px-4 py-2 flex items-center font-medium ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                  {t('admin.mapping.refresh')}
                </div>
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2">
            {MAPPING_TYPES.map((type) => {
              const Icon = type.icon;
              const isActive = activeTab === type.key;
              const tabColors = getColorClasses(type.color);

              return (
                <button
                  key={type.key}
                  onClick={() => setActiveTab(type.key)}
                  className={`px-4 py-2 rounded-xl font-medium text-sm flex items-center space-x-2 transition-all duration-200 border-2 ${
                    isActive
                      ? tabColors.tabActive
                      : isDarkMode
                        ? 'bg-gray-800/30 text-white/60 border-transparent hover:bg-gray-700/40 hover:text-white/80'
                        : 'bg-slate-100 text-slate-600 border-transparent hover:bg-slate-200 hover:text-slate-800'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{type.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Add New Mapping */}
      <div className="relative group/add">
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
          : 'glass-light-card'
        } rounded-3xl`}></div>

        <div className="relative p-6">
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {t('admin.mapping.addNew', { type: currentType?.codeLabel })}
          </h3>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                {currentType?.codeLabel}
              </label>
              <input
                type="text"
                value={newMapping.code}
                onChange={(e) => setNewMapping({ ...newMapping, code: e.target.value })}
                placeholder={currentType?.codePlaceholder}
                className={`w-full px-4 py-3 rounded-xl ${isDarkMode
                  ? 'bg-gray-800/50 border-white/20 text-white placeholder-white/40 backdrop-blur-xl'
                  : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400'
                } border focus:ring-2 ${colorClasses.ring} focus:border-transparent transition-all duration-200`}
              />
            </div>
            <div className="flex-1">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                {currentType?.nameLabel}
              </label>
              <input
                type="text"
                value={newMapping.name}
                onChange={(e) => setNewMapping({ ...newMapping, name: e.target.value })}
                placeholder={currentType?.namePlaceholder}
                className={`w-full px-4 py-3 rounded-xl ${isDarkMode
                  ? 'bg-gray-800/50 border-white/20 text-white placeholder-white/40 backdrop-blur-xl'
                  : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400'
                } border focus:ring-2 ${colorClasses.ring} focus:border-transparent transition-all duration-200`}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleAddMapping}
                disabled={saving || !newMapping.code || !newMapping.name}
                className={`relative group/btn overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:transform-none ${isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8]'}`}
                style={{
                  background: isDarkMode ? colorClasses.button : undefined,
                  backdropFilter: isDarkMode ? 'blur(20px)' : undefined,
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : undefined,
                  boxShadow: isDarkMode && colorClasses.buttonShadow ? `${colorClasses.buttonShadow}, inset 0 1px 0 rgba(255,255,255,0.2)` : undefined
                }}
              >
                <div className="relative z-10 px-6 py-3 flex items-center font-medium text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('common.add')}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mappings List */}
      <div className="relative group/list">
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
          : 'glass-light-card'
        } rounded-3xl`}></div>

        <div className="relative p-6">
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {t('admin.mapping.existing', { type: currentType?.label, count: currentMappings.length })}
          </h3>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-current border-t-transparent" style={{ borderColor: currentType?.color === 'cyan' ? '#06b6d4' : currentType?.color === 'green' ? '#22c55e' : currentType?.color === 'purple' ? '#9333ea' : '#f97316' }}></div>
            </div>
          ) : currentMappings.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>
              <TypeIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t('admin.mapping.empty', { type: currentType?.label })}</p>
              <p className="text-sm mt-1">{t('admin.mapping.emptyHint')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {currentMappings.map((mapping) => (
                <div
                  key={mapping.id}
                  className={`relative group/item overflow-hidden rounded-2xl transition-all duration-300 ${isDarkMode
                    ? 'bg-gray-800/30 border border-white/10 hover:border-white/20'
                    : 'bg-white border border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                    <div className="flex-1">
                      <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                        {currentType?.codeLabel}
                      </label>
                      <input
                        type="text"
                        value={mapping.code}
                        onChange={(e) => handleUpdateMapping(mapping.id, 'code', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg ${isDarkMode
                          ? 'bg-gray-700/50 border-white/10 text-white'
                          : 'bg-white border-slate-200 text-slate-800'
                        } border focus:ring-2 ${colorClasses.ring} focus:border-transparent transition-all duration-200`}
                      />
                    </div>
                    <div className="flex-1">
                      <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                        {currentType?.nameLabel}
                      </label>
                      <input
                        type="text"
                        value={mapping.name}
                        onChange={(e) => handleUpdateMapping(mapping.id, 'name', e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg ${isDarkMode
                          ? 'bg-gray-700/50 border-white/10 text-white'
                          : 'bg-white border-slate-200 text-slate-800'
                        } border focus:ring-2 ${colorClasses.ring} focus:border-transparent transition-all duration-200`}
                      />
                    </div>
                    <div className="flex items-center space-x-2 mt-4 sm:mt-0">
                      <button
                        onClick={() => handleSaveMapping(mapping)}
                        disabled={saving}
                        className={`p-2 rounded-xl transition-all duration-200 ${colorClasses.saveBtn}`}
                        title={t('common.save')}
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteMapping(mapping.id)}
                        className={`p-2 rounded-xl transition-all duration-200 ${isDarkMode
                          ? 'bg-red-600/30 hover:bg-red-600/50 text-red-300'
                          : 'bg-red-100/60 hover:bg-red-200/70 text-red-600'
                        }`}
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MappingSettings;
